import * as React from 'react';
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, CheckCircle2 } from 'lucide-react';
import { PageHeader } from '../../../components/page-header';
import { Button } from '../../../components/ui/button';
import { useToast } from '../../../components/ui/toast';
import {
  SeatInfo,
  getSeatInfo,
  reactivateSubscription,
} from '../../../../application/routes/settings/components/PaymentMethods/paymentApi';
import { DEV_MOCK, MOCK_SEAT_INFO } from './mock';

declare const global: {
  me: {
    id: string;
    orgID: string;
    imageName?: string;
    name: string;
  };
};

export function CancellationConfirmedPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const orgId: string | undefined = (global as any)?.me?.orgID;

  const [seatInfo, setSeatInfo] = useState<SeatInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [reactivating, setReactivating] = useState(false);

  const fetchSeatInfo = useCallback(async () => {
    if (DEV_MOCK) {
      setSeatInfo({
        ...MOCK_SEAT_INFO,
        cancelAtPeriodEnd: true,
      } as unknown as SeatInfo);
      setLoading(false);
      return;
    }
    if (!orgId) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const res = await getSeatInfo(orgId);
      if (res.success && res.data) {
        setSeatInfo(res.data);
      }
    } catch {
      // Non-critical, we can still show the page
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    fetchSeatInfo();
  }, [fetchSeatInfo]);

  const handleReactivate = async () => {
    if (DEV_MOCK) {
      toast.success('Subscription reactivated');
      navigate('/v2/settings/billing');
      return;
    }
    if (!orgId) {
      return;
    }
    try {
      setReactivating(true);
      const res = await reactivateSubscription(orgId);
      if (res.success) {
        toast.success('Subscription reactivated');
        navigate('/v2/settings/billing');
      } else {
        toast.error(res.error || 'Failed to reactivate subscription');
      }
    } catch {
      toast.error('Unable to reactivate. Please try again.');
    } finally {
      setReactivating(false);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const endDate =
    seatInfo?.subscriptionStatus === 'trialing'
      ? seatInfo?.trialEndDate
      : seatInfo?.nextBillingDate;

  if (loading) {
    return (
      <div className="flex flex-col h-full">
        <PageHeader
          title="Plan Cancelled"
          avatar={{ src: global.me?.imageName, name: global.me?.name }}
        />
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="size-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        title="Plan Cancelled"
        avatar={{ src: global.me?.imageName, name: global.me?.name }}
      />

      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-2xl mx-auto space-y-8">
          {/* Confirmation */}
          <div className="text-center space-y-4">
            <div className="mx-auto size-16 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
              <CheckCircle2 className="size-8 text-green-600 dark:text-green-400" />
            </div>
            <h1 className="font-serif text-[28px] font-normal leading-tight tracking-[-0.02em] text-foreground">
              Your plan has been cancelled
            </h1>
            <p className="text-muted-foreground max-w-md mx-auto">
              You&apos;ll have access to FieldInspect until{' '}
              <strong className="text-foreground">
                {endDate
                  ? formatDate(endDate)
                  : 'the end of your billing period'}
              </strong>
              . After that, your data will be saved for 90 days — you can
              reactivate any time.
            </p>
          </div>

          {/* Reactivate CTA */}
          <div className="flex justify-center">
            <Button onClick={handleReactivate} disabled={reactivating}>
              {reactivating ? (
                <Loader2 className="size-4 animate-spin mr-2" />
              ) : null}
              Reactivate my plan
            </Button>
          </div>

          {/* Survey prompt */}
          <div className="border border-border rounded-xl p-6 bg-card text-center space-y-4">
            <p className="text-base font-semibold text-foreground">
              One last thing — what made you cancel?
            </p>
            <p className="text-sm text-muted-foreground">
              Your feedback helps us build a better product. This takes 10
              seconds.
            </p>
            <Button
              variant="outline"
              onClick={() => navigate('/v2/settings/billing/cancel/survey')}
            >
              Share feedback
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
